import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MyAvailability as Data } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { Alert, Button } from '../ui';

/** Players and parents: say you (or your child) can't make a session. */
export function MyAvailability({ teamId, sessionId }: { teamId: string; sessionId: string }) {
  const qc = useQueryClient();
  const key = ['availability', sessionId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<Data>(`/teams/${teamId}/sessions/${sessionId}/availability`) });
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [asking, setAsking] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: (b: { playerId: string; unavailable: boolean; reason?: string }) => api.put<Data>(`/teams/${teamId}/sessions/${sessionId}/availability`, b),
    onSuccess: (d) => {
      qc.setQueryData(key, d);
      setAsking(null);
      void qc.invalidateQueries({ queryKey: ['me-availability'] });
      void qc.invalidateQueries({ queryKey: ['away-counts'] });
    },
  });

  if (!q.data?.players.length) return null;
  return (
    <section className="mb-6 rounded-lg border border-line bg-surface px-4 py-3">
      <h2 className="text-[15px] font-semibold">Coming?</h2>
      {set.isError && <div className="mt-2"><Alert>{errorMessage(set.error)}</Alert></div>}
      <ul className="mt-2 space-y-3">
        {q.data.players.map((p) => (
          <li key={p.id}>
            {p.unavailable ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <p className="flex-1">
                  <span className="font-medium">{p.name}</span>{' '}
                  {p.away
                    ? `is away until ${new Date(`${p.away.endDate}T00:00:00Z`).toLocaleDateString('en-AU', { timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long' })}${p.away.reason ? ` (${p.away.reason})` : ''}.`
                    : `can’t make it${p.reason ? `: ${p.reason}` : ''}.`}
                </p>
                <Button variant="secondary" loading={set.isPending} onClick={() => set.mutate({ playerId: p.id, unavailable: false })}>
                  Can make it after all
                </Button>
              </div>
            ) : asking === p.id ? (
              <form
                className="space-y-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  set.mutate({ playerId: p.id, unavailable: true, reason: reasons[p.id] });
                }}
              >
                <label className="block space-y-1">
                  <span className="block text-[14px]">Reason for {p.name} (optional, coaches see it)</span>
                  <input className="field-input" maxLength={200} value={reasons[p.id] ?? ''} onChange={(e) => setReasons({ ...reasons, [p.id]: e.target.value })} placeholder="e.g. school camp" autoFocus />
                </label>
                <div className="flex gap-2">
                  <Button type="submit" loading={set.isPending}>
                    Tell the coach
                  </Button>
                  <Button type="button" variant="secondary" onClick={() => setAsking(null)}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <p className="flex-1">
                  <span className="font-medium">{p.name}</span> is coming.
                </p>
                <Button variant="secondary" onClick={() => setAsking(p.id)}>
                  Can’t make it
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
